import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import axios from "axios";
import getDomainURL from "utils/api";
import { processSite } from "utils/host";
import { notification } from "antd";
import moment from "moment";
import { convertBase64ToObject } from "utils/objToBase64";

const ACTIONS = {
  SET_H2H_SALES_OVERVIEW: "SET_H2H_SALES_OVERVIEW",
};

const mapStateToProps = (state) => ({
  data: state,
  colors: state.ClientReducer.clientInfo.styles?.colors,
  user: state?.user,
  modulePermissions: state?.modulePermissions || {},
  h2hSalesData: state?.analysis?.h2hSalesData || [],
  h2hSalesMonths: state?.analysis?.h2hSalesMonths || [],
  selectedLanguage:
    state?.ProfileReducer?.profile?.preferred_language ||
    state?.language?.selectedLanguage,
  clientInfo: state.ClientReducer.clientInfo,
  siteInfo: state.ClientReducer.siteInfo,
  profile: state.ProfileReducer.profile,
  country: state.ProfileReducer.country,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getH2HSalesOverview: (queryBase64) => async (dispatch, getState) => {
        try {
          const state = getState();
          const siteId =
            state?.site?.site?.id ||
            state?.sites?.selectedSite?.id ||
            state?.user?.site_id ||
            state?.ClientReducer?.siteInfo?.id ||
            state?.ProfileReducer?.profile?.site_id;

          if (!siteId) throw new Error("Missing site_id in FE state.");

          const token =
            localStorage["IMPERSONATE_TOKEN"] || localStorage["TECHBASE_TOKEN"];

          const requestUrl = `${getDomainURL()}/sites/${siteId}/analysis/h2h-sales-overview`;

          // ✅ Decode query ONCE (we need warehouse_id from here)
          let queryObj = {};
          try {
            queryObj = convertBase64ToObject(queryBase64 || "");
          } catch (e) {}

          console.debug("H2H decoded query.filter:", queryObj?.filter);

          // ✅ Warehouse rules
          const profile = state?.ProfileReducer?.profile || {};
          const roleName = String(profile?.site_role?.name || "");
          const access = profile?.site_role?.warehouse_access ?? [];
          const hasRestricted = Array.isArray(access) && access.length > 0;

          // Admin = role name has admin OR access list empty/unrestricted
          const isAdmin = /admin/i.test(roleName) || !hasRestricted;

          // Read selected warehouse from query
          const requestedWarehouseIdRaw =
            queryObj?.filter?.warehouse_id ?? queryObj?.filter?.site_warehouse_id;

          // Normalize "-1" => ALL => null
          const requestedWarehouseId =
            requestedWarehouseIdRaw != null && Number(requestedWarehouseIdRaw) === -1
              ? null
              : requestedWarehouseIdRaw;

          // ✅ This is the ONLY id used for FE filtering
          // - Non-admin: always locked to bound warehouse
          // - Admin: selected warehouse id OR null for ALL
          const currentWarehouseId = (() => {
            if (!isAdmin) {
              const bound =
                profile?.site_warehouse_id || state?.user?.site_warehouse_id;
              return bound ? String(bound) : null;
            }
            return requestedWarehouseId != null ? String(requestedWarehouseId) : null;
          })();

          console.debug("H2H isAdmin:", isAdmin);
          console.debug("H2H requestedWarehouseId:", requestedWarehouseId);
          console.debug("H2H currentWarehouseId used:", currentWarehouseId);

          // ✅ Call API (KEEP your headers, don’t break auth/site routing)
          const res = await axios.get(requestUrl, {
            params: { query: queryBase64 },
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
              Site: processSite(window.location.host),
            },
          });

          const result = res?.data?.result || [];
          const monthKeyed = res?.data?.month_keyed || null;
          console.debug("H2H API raw result:", result);

          // ---- Determine months + rows ----
          let months = [];
          let rows = [];

          if (!Array.isArray(result) && typeof result === "object") {
            months = Object.keys(result).sort();
            rows = null;
          } else {
            rows = result || [];
            const monthsSet = new Set();
            rows.forEach((r) => {
              if (r.sale_month) monthsSet.add(String(r.sale_month).slice(0, 7));
            });
            months = Array.from(monthsSet).sort();
          }

          // If API returned no months, derive months from the date filter
          if (!months || months.length === 0) {
            const between = queryObj?.filter?.created_at__between;
            if (Array.isArray(between) && between[0] && between[1]) {
              const start = moment(between[0]);
              const end = moment(between[1]);
              const derived = [];
              const cur = start.clone().startOf("month");
              while (cur.isSameOrBefore(end, "month")) {
                derived.push(cur.format("YYYY-MM"));
                cur.add(1, "month");
              }
              if (derived.length > 0) months = derived;
            }
            if (!months || months.length === 0) months = [moment().format("YYYY-MM")];
          }

          const pickNumber = (obj, keys) => {
            for (const k of keys) {
              if (obj && obj[k] != null) return Number(obj[k]) || 0;
            }
            return 0;
          };

          const data = [
            { dataKey: "totalSales", label: "Total Sales", type: "number" },
            { dataKey: "dailyRecruit", label: "Daily Recruit", type: "number" },
            {
              dataKey: "basketSize",
              label: "Basket Size (existing dealers & new recruits)",
              type: "number",
            },
            {
              dataKey: "numTransactingDealers",
              label: "Number of Transacting Dealers",
              type: "number",
            },
            {
              dataKey: "newRecruitPurchase",
              label: "New Recruit Purchase",
              type: "number",
            },
          ].map((r) => ({ ...r }));

          // ---- Month-keyed object response ----
          if (!Array.isArray(result) && typeof result === "object") {
            months.forEach((m) => {
              const monthData = result[m] || {};

              const getVal = (obj, paths) => {
                for (const p of paths) {
                  const v = obj?.[p];
                  if (v == null) continue;
                  if (typeof v === "object") {
                    const east = Number(v?.east ?? 0);
                    const west = Number(v?.west ?? 0);
                    return east + west;
                  }
                  return Number(v) || 0;
                }
                return 0;
              };

              const totalSales = getVal(monthData, [
                "totalSales",
                "total_sales",
                "total_order_value",
                "amount",
              ]);
              const dailyRecruit = getVal(monthData, [
                "new_recruits",
                "new_recruit_count",
                "new_user_count",
                "recruits",
              ]);
              const newRecruitPurchase = getVal(monthData, [
                "new_recruit_purchase",
                "new_user_purchase",
                "new_user_with_purchase_total",
              ]);
              const totalOrders = getVal(monthData, [
                "totalOrders",
                "total_orders",
                "total_transactions",
                "num_orders",
                "order_count",
              ]);
              const numTransactingDealers = getVal(monthData, [
                "transacting_dealers",
                "num_transacting_dealers",
                "number_of_transacting_dealers",
                "dealer_count",
                "num_dealers",
              ]);

              const basketSize = numTransactingDealers ? totalSales / numTransactingDealers : 0;

              data.find((d) => d.dataKey === "totalSales")[m] = totalSales;
              data.find((d) => d.dataKey === "dailyRecruit")[m] = dailyRecruit;
              data.find((d) => d.dataKey === "newRecruitPurchase")[m] = newRecruitPurchase;
              data.find((d) => d.dataKey === "numTransactingDealers")[m] = numTransactingDealers;
              data.find((d) => d.dataKey === "basketSize")[m] = Number(basketSize.toFixed(2));
            });
          } else {
            // ---- Array rows: aggregate per month + apply warehouse selection ----
            months.forEach((m) => {
              const monthRows = rows.filter(
                (row) => String(row.sale_month).slice(0, 7) === m
              );

              // Debug distinct warehouses returned by API for this month
              const whSet = new Set(
                monthRows.map((rr) =>
                  String(rr.site_warehouse_id || rr.warehouse_id || rr.site_warehouse?.id || "")
                )
              );
              console.debug(`H2H month ${m} warehouses in API:`, Array.from(whSet));

const filtered = monthRows;


              const totalSales = filtered.reduce(
                (s, rr) =>
                  s + pickNumber(rr, ["total_sales", "total_order_value", "amount", "sales_total"]),
                0
              );
              const dailyRecruit = filtered.reduce(
                (s, rr) =>
                  s + pickNumber(rr, ["new_recruits", "recruit_count", "new_recruit_count", "new_user_count", "recruits"]),
                0
              );
              const newRecruitPurchase = filtered.reduce(
                (s, rr) =>
                  s + pickNumber(rr, ["new_recruit_purchase", "new_user_purchase", "new_user_with_purchase_total"]),
                0
              );
              const totalOrders = filtered.reduce(
                (s, rr) =>
                  s + pickNumber(rr, ["total_orders", "total_transactions", "num_orders", "order_count"]),
                0
              );
          let numTransactingDealers = 0;
          if (monthKeyed && monthKeyed[m]) {
            numTransactingDealers = pickNumber(monthKeyed[m], ["transacting_dealers"]);
            console.debug(`H2H month ${m} using monthKeyed transacting_dealers:`, numTransactingDealers);
          } else {
            numTransactingDealers = filtered.reduce(
              (s, rr) =>
                s + pickNumber(rr, ["transacting_dealers", "num_transacting_dealers", "number_of_transacting_dealers", "dealer_count", "num_dealers"]),
              0
            );
          }
              

              const basketSize = numTransactingDealers ? totalSales / numTransactingDealers : 0;

              data.find((d) => d.dataKey === "totalSales")[m] = totalSales;
              data.find((d) => d.dataKey === "dailyRecruit")[m] = dailyRecruit;
              data.find((d) => d.dataKey === "newRecruitPurchase")[m] = newRecruitPurchase;
              data.find((d) => d.dataKey === "numTransactingDealers")[m] = numTransactingDealers;
              data.find((d) => d.dataKey === "basketSize")[m] = Number(basketSize.toFixed(2));

              console.debug(`H2H month ${m} filteredCount:`, filtered.length);
            });
          }

          console.debug("H2H dispatch payload:", { data, months });

          dispatch({
            type: ACTIONS.SET_H2H_SALES_OVERVIEW,
            payload: { data, months },
          });
        } catch (e) {
          console.error(e);
          notification.error({
            message: "H2H Sales Overview",
            description: e?.message || "Failed to load H2H Sales Overview.",
          });
        }
      },
    },
    dispatch
  );

export const h2hSalesReducer = (state = {}, action) => {
  switch (action.type) {
    case ACTIONS.SET_H2H_SALES_OVERVIEW:
      return {
        ...state,
        h2hSalesData: action.payload.data,
        h2hSalesMonths: action.payload.months,
      };
    default:
      return state;
  }
};

export default (Comp) => connect(mapStateToProps, mapDispatchToProps)(Comp);
